/**
 * Side effects when app state changes.
 * 1. Logout → drop tournament data. (Configure is sent from the auth-success
 *    handler so it re-runs on every connect, including reconnects.)
 * 2. Auth + config set → subscribe to tournaments (re-subscribe if language/currency change).
 */

import { shallow } from "zustand/shallow";

import { useAppStore } from "./app.store";
import i18n, { resources } from "@/i18n";
import { resolveResourceLanguage } from "@/utils/language";
import { tournamentLeaderboardsHandler } from "@/services/websocket/handlers/tournament-leaderboards.handler";
import { tournamentProgressHandler } from "@/services/websocket/handlers/tournament-progress.handler";
import { campaignsHandler } from "@/services/websocket/handlers/campaigns.handler";
import { campaignProgressHandler } from "@/services/websocket/handlers/campaign-progress.handler";
import { sprintMissionPoolHandler } from "@/services/websocket/handlers/sprint-mission-pool.handler";
import { jackpotProgressHandler } from "@/services/websocket/handlers/jackpot-progress.handler";
import { raffleProgressHandler } from "@/services/websocket/handlers/raffle-progress.handler";
import { prizeDropPrizesHandler } from "@/services/websocket/handlers/prize-drop-prizes.handler";
import { missionProgressHandler } from "@/services/websocket/handlers/mission-progress.handler";
import { notificationsHandler } from "@/services/websocket/handlers/notifications.handler";
import { rekeyWonNotifications } from "@/services/notifications";

/** Every data stream the widget subscribes to once auth + config are ready.
 *  Tournaments' structural data rides `campaigns`; only the volatile leaderboard
 *  (`tournament_leaderboards`) + the per-player score (`tournament_progress`) are
 *  tournament-specific streams. */
const STREAM_HANDLERS = [
  tournamentLeaderboardsHandler,
  tournamentProgressHandler,
  campaignsHandler,
  campaignProgressHandler,
  prizeDropPrizesHandler,
  sprintMissionPoolHandler,
  jackpotProgressHandler,
  raffleProgressHandler,
  missionProgressHandler,
  notificationsHandler,
];

function isConfigSet(
  language: string | null,
  currency: string | null
): boolean {
  return language !== null || currency !== null;
}

/**
 * Sets up listeners reacting to app store state changes.
 */
export function setupStoreSubscriptions(): void {
  useAppStore.subscribe(
    (state) => state.authenticated,
    (authenticated, prevAuthenticated) => {
      if (!authenticated && prevAuthenticated) {
        // Logout / credentials removed: drop all campaign data so the UI shows
        // an empty (logged-out) state instead of stale campaigns. unsubscribe()
        // resets the `subscribed` flag too, so the next login re-subscribes
        // cleanly. A transient disconnect does NOT flip `authenticated`, so the
        // data stays visible while reconnecting.
        STREAM_HANDLERS.forEach((h) => h.unsubscribe());
      }
    }
  );

  // Keep i18next in sync with the config-store language. The initial language
  // is resolved synchronously at mount (see i18n.ts); this handles later
  // changes delivered via the websocket configure flow.
  useAppStore.subscribe(
    (state) => state.language,
    (language) => {
      if (!language) return;

      // The store holds a wire code (`zh-hans`); i18next is keyed by bundled
      // locale filename (`zh`), so map before comparing or switching.
      const lng = resolveResourceLanguage(language, Object.keys(resources));

      if (lng !== i18n.language) {
        void i18n.changeLanguage(lng);
      }
    }
  );

  /**
   * Sets up listeners reacting to app store state changes.
   * - When authentication, language, or currency change, manages all campaign
   *   stream (un)subscriptions (currency/language are re-negotiated per connection).
   */
  useAppStore.subscribe(
    (state) => ({
      authenticated: state.authenticated,
      language: state.language,
      currency: state.currency,
    }),
    (curr, prev) => {
      const ready =
        curr.authenticated && isConfigSet(curr.language, curr.currency);
      if (!ready) return;

      const wasReady =
        prev.authenticated && isConfigSet(prev.language, prev.currency);
      const configChanged =
        curr.language !== prev.language || curr.currency !== prev.currency;
      if (wasReady && !configChanged) return;

      if (wasReady && configChanged) {
        STREAM_HANDLERS.forEach((h) => h.unsubscribe());
      }
      STREAM_HANDLERS.forEach((h) => h.subscribe());
    },
    // The selector returns a fresh object each store write; without a shallow
    // equalityFn the listener would run on every write, not just when these
    // three fields change.
    { equalityFn: shallow }
  );

  // Queued prize wins resolve their campaign key from `campaignsById`; on
  // (re)connect the notifications page can land first, leaving wins keyed by
  // the numeric-id fallback. Re-resolve whenever the campaigns page changes.
  useAppStore.subscribe(
    (state) => state.campaignsById,
    () => rekeyWonNotifications()
  );
}
